import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api } from "../lib/utils";
import FileUpload from "../components/ui/file-upload";
import { toast } from "sonner";
import { Save, ArrowLeft } from "lucide-react";
import { useGetAllCategories } from "../customhooks/useGetAllCategories";
import { useGetAllSubCategories } from "../customhooks/useGetAllSubCategories";

const EditTicket = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const parseUser = typeof user === "string" ? JSON.parse(user) : user;
  const [formData, setFormData] = useState({
    category_id: "",
    sub_category_id: "",
    subject: "",
    description: "",
    department: "",
    priority: "MEDIUM",
    category: "",
    subcategory: "",
    comment: "",
    status: "",
    updatedBy: "",
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [reloadTickets, setReloadTickets] = useState(false);
  const filterNewFiles = allFiles.filter((elem) => elem?.originalName);
  const useLocationState = useLocation();
  const categories = useGetAllCategories(formData ? formData?.department : "");
  const subCategories = useGetAllSubCategories(
    formData ? formData?.category : "",
  );

  const navigateRoleWise = () => {
    navigate(
      parseUser?.Department === "HRD" ||
        parseUser?.Department === "HR" ||
        parseUser?.Department === "Finance" ||
        parseUser?.Department === "Administration" ||
        parseUser?.Department === "Management"
        ? "/dashboard"
        : "/my-tickets",
    );
  };

  const fetchTicket = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/tickets/${id}${useLocationState?.state ? "?myTickets=true" : ""}`,
      );
      const ticketData = response.ticket || response;

      setTicket(ticketData);

      // Extract actual subject by removing the [Category - SubCategory] prefix
      let cleanSubject = ticketData.subject || "";
      cleanSubject = cleanSubject.replace(/^\[[^\]]*\]\s*/, "").trim();

      setFormData({
        subject: cleanSubject,
        description: ticketData.description || "",
        department: ticketData.department || "",
        priority: ticketData.priority || "MEDIUM",
        category: ticketData.category || "",
        subcategory: ticketData.subcategory || "",
        comment: ticketData.comment || "",
        status: ticketData.status || "",
        updatedBy: parseUser?.actort,
        category_id: ticketData.category_id,
        sub_category_id: ticketData.sub_category_id,
      });

      // Load existing files from backend
      setAllFiles(ticketData.files || []);
      setReloadTickets(false);
    } catch (error) {
      console.error("Failed to fetch ticket:", error);
      toast.error("Failed to load ticket details");
      navigateRoleWise();
    } finally {
      setLoading(false);
    }
  }, [id, user, navigate]);

  useEffect(() => {
    fetchTicket();
  }, [id, fetchTicket, reloadTickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      // Prepare update data including file operations
      const updateData = {
        ...formData,
        files: filterNewFiles,
      };

      if (
        !formData?.department ||
        !formData?.category ||
        !formData?.subcategory
      ) {
        return toast.error(
          `Please select ${
            !formData?.department
              ? "department"
              : !formData?.category
              ? "category"
              : "subcategory"
          }`,
        );
      }

      await api.put(`/api/tickets/${id}`, updateData);
      toast.success("Ticket updated successfully!");
      navigateRoleWise();
    } catch (error) {
      toast.error("Failed to update ticket");
      console.error("Failed to update ticket:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Reset category and subcategory when department changes
      if (field === "department") {
        newData.category = "";
        newData.subcategory = "";
      } else if (field === "category") {
        newData.subcategory = "";
      }

      return newData;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-4xl px-4">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Edit Ticket</h1>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center py-8">Loading ticket details...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-4xl px-4">
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={navigateRoleWise}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            >
              <ArrowLeft className="h-4 w-4" />
              {`Back to ${
                parseUser?.Department === "HRD" ||
                parseUser?.Department === "HR" ||
                parseUser?.Department === "Finance" ||
                parseUser?.Department === "Administration" ||
                parseUser?.Department === "Management"
                  ? "Dashboard"
                  : "My Tickets"
              }`}
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Edit Ticket{" "}
            {formData.department === "ADMIN"
              ? "INFRA"
              : formData.department.slice(0, 3)}
            #000{ticket?.id}
          </h1>
          <p className="text-gray-600 mt-2">
            Make changes to your ticket details
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Ticket Details
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full-width fields */}
            <div className="space-y-4">
              {/* Subject */}
              <div>
                <Label
                  htmlFor="subject"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Subject <span className="text-red-500">*</span>
                </Label>
                {ticket?.createdBy === Number(parseUser?.acr) ? (
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={handleInputChange("subject")}
                    placeholder="Brief description of the issue"
                    required
                    className="w-full"
                  />
                ) : (
                  <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-md text-sm">
                    <span className="font-medium">
                      {formData.subject?.replace("_", " ") || "Not Set"}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <Label
                  htmlFor="description"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Description <span className="text-red-500">*</span>
                </Label>
                {ticket?.createdBy === Number(parseUser?.acr) ? (
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={handleInputChange("description")}
                    placeholder="Detailed description of the issue"
                    rows={4}
                    required
                    className="w-full resize-none"
                  />
                ) : (
                  <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-md text-sm">
                    <span className="font-medium">
                      {formData.description?.replace("_", " ") || "Not Set"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Two-column grid for dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Priority */}
              <div>
                <Label
                  htmlFor="priority"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Priority
                </Label>
                {ticket?.createdBy === Number(parseUser?.acr) ? (
                  <Select
                    value={formData.priority}
                    onValueChange={handleInputChange("priority")}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-md text-sm">
                    <span className="font-medium">
                      {formData.priority?.replace("_", " ") || "Not Set"}
                    </span>
                  </div>
                )}
              </div>

              {/* Department */}
              <div>
                <Label
                  htmlFor="department"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Department
                </Label>
                {ticket?.createdBy === Number(parseUser?.acr) ? (
                  <Select
                    value={formData.department}
                    onValueChange={handleInputChange("department")}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ADMIN">Infra</SelectItem>
                      <SelectItem value="FINANCE">Finance</SelectItem>
                      <SelectItem value="HR">HR</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-md text-sm">
                    <span className="font-medium">
                      {formData.department === "ADMIN"
                        ? "INFRA"
                        : formData?.department || "Not Set"}
                    </span>
                  </div>
                )}
              </div>

              {/* Category - Shows only after Department is selected */}
              {formData.department && (
                <div className="animate-in slide-in-from-top duration-300">
                  <Label
                    htmlFor="category"
                    className="text-sm font-medium text-gray-700 mb-1.5 block"
                  >
                    Category
                  </Label>
                  {ticket?.createdBy === Number(parseUser?.acr) ? (
                    <Select
                      value={
                        formData.category_id ? String(formData.category_id) : ""
                      }
                      onValueChange={(id) => {
                        const selected = categories.find(
                          (c) => String(c.id) === String(id),
                        );
                        setFormData((prev) => ({
                          ...prev,
                          category_id: selected?.id || "",
                          category: selected?.category_name || "",
                          subcategory: "",
                        }));
                      }}
                      required
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Category">
                          {categories.find(
                            (c) =>
                              String(c.id) === String(formData.category_id),
                          )?.category_name || formData.category}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category?.id}>
                            {category?.category_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-md text-sm">
                      <span className="font-medium">
                        {formData.category?.replace("_", " ") || "Not Set"}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Subcategory - Shows only after Category is selected */}
              {formData.category && formData.department && (
                <div className="animate-in slide-in-from-top duration-300">
                  <Label
                    htmlFor="subcategory"
                    className="text-sm font-medium text-gray-700 mb-1.5 block"
                  >
                    Subcategory
                  </Label>
                  {ticket?.createdBy === Number(parseUser?.acr) ? (
                    <Select
                      value={
                        formData.sub_category_id
                          ? String(formData.sub_category_id)
                          : ""
                      }
                      // onValueChange={handleInputChange("subcategory")}
                      onValueChange={(id) => {
                        const selected = subCategories.find(
                          (c) => String(c.id) === String(id),
                        );
                        setFormData((prev) => ({
                          ...prev,
                          sub_category_id: selected?.id || "",
                          subcategory: selected?.sub_category_name,
                        }));
                      }}
                      required
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Subcategory">
                          {
                            subCategories.find(
                              (c) =>
                                String(c.id) ===
                                String(formData.sub_category_id),
                            )?.sub_category_name
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {subCategories.map((subcategory) => (
                          <SelectItem key={subcategory} value={subcategory?.id}>
                            {subcategory?.sub_category_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-md text-sm">
                      <span className="font-medium">
                        {formData.subcategory?.replace("_", " ") || "Not Set"}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Status - Editable for managers, read-only for employees */}
              <div className="animate-in slide-in-from-top duration-300">
                <Label
                  htmlFor="status"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Status
                </Label>
                {ticket?.createdBy !== Number(parseUser?.acr) ? (
                  <Select
                    value={formData.status}
                    onValueChange={handleInputChange("status")}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OPEN">Open</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="ON_HOLD">On Hold</SelectItem>
                      <SelectItem value="CLOSED">Closed</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-md text-sm">
                    <span className="font-medium">
                      {formData.status?.replace("_", " ") || "Not Set"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* File Upload Section */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Upload Files
              </Label>
              <FileUpload
                value={allFiles}
                onChange={setAllFiles}
                onChangeTickets={setReloadTickets}
                areTicketsReload={reloadTickets}
                maxFiles={5}
                maxSizeMB={10}
                disabled={updating}
                ticketId={ticket?.id}
              />
            </div>

            {/* Comment Field */}
            <div>
              <Label
                htmlFor="comment"
                className="text-sm font-medium text-gray-700 mb-1.5 block"
              >
                Comment <span className="text-gray-400">(Optional)</span>
              </Label>
              <Textarea
                id="comment"
                value={formData.comment}
                onChange={handleInputChange("comment")}
                placeholder="Add any additional comments or notes..."
                rows={3}
                className="w-full resize-none font-poppins"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button type="submit" disabled={updating} className="px-6">
                {updating ? (
                  "Updating..."
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Update Ticket
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={navigateRoleWise}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditTicket;
